// Smart Sash Admin Panel - RFID Tags Page
// Admin listing of every RFID tag in the system (GET /api/rfid/tags).

import { Alert, Button } from '@/components/common';
import {
  RfidTagAssignModal,
  RfidTagCreateModal,
  RfidTagDeactivateConfirmModal,
  RfidTagFiltersBar,
  RfidTagsTable,
} from '@/components/rfid-tags';
import { useRfidTagManagement } from '@/hooks/useRfidTagManagement';
import React from 'react';

export const RfidTags: React.FC = () => {
  const {
    tags,
    pagination,
    isLoading,
    error,
    errorStatus,
    labs,
    groups,
    institutions,
    filters,
    hasActiveFilters,
    currentPage,
    pageSize,
    pageSizeOptions,
    canChooseInstitution,
    canToggleActive,
    canCreateCard,
    modalInstitutions,
    unlinkingCardId,
    users,
    isLoadingUsers,
    tagToAssign,
    isAssigningCard,
    togglingCardId,
    tagToDeactivate,
    isCreateModalOpen,
    isCreatingCard,
    setFilter,
    resetFilters,
    setCurrentPage,
    handlePageSizeChange,
    clearError,
    retry,
    handleUnlinkCard,
    handleAssignClick,
    handleConfirmAssign,
    closeAssignModal,
    handleToggleActive,
    handleConfirmDeactivate,
    closeDeactivateModal,
    openCreateModal,
    closeCreateModal,
    handleCreateCard,
  } = useRfidTagManagement();

  // 403 surfaces as the dedicated "no access" panel; everything else is a banner.
  const isForbidden = errorStatus === 403;

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 mb-0">RFID Tags</h1>
              <p className="text-muted mb-0">
                All RFID tags in the system, with their current assignment and scope.
              </p>
            </div>
            {canCreateCard && !isForbidden && (
              <Button variant="primary" icon="fas fa-plus" onClick={openCreateModal}>
                New RFID
              </Button>
            )}
          </div>
        </div>
      </div>

      {isForbidden ? (
        <div className="row">
          <div className="col-12">
            <Alert variant="warning">
              <i className="fas fa-lock me-2"></i>
              You do not have access to this page. The <code>Rfid.ViewAll</code> permission is
              required.
            </Alert>
          </div>
        </div>
      ) : (
        <>
          <RfidTagFiltersBar
            filters={filters}
            labs={labs}
            groups={groups}
            institutions={institutions}
            canChooseInstitution={canChooseInstitution}
            hasActiveFilters={hasActiveFilters}
            onChange={setFilter}
            onReset={resetFilters}
          />

          {error && (
            <div className="row mb-3">
              <div className="col-12">
                <Alert variant="danger" dismissible onDismiss={clearError}>
                  <div className="d-flex justify-content-between align-items-center">
                    <span>
                      <i className="fas fa-exclamation-circle me-2"></i>
                      {error}
                    </span>
                    <Button variant="danger" size="sm" icon="fas fa-redo" onClick={retry}>
                      Retry
                    </Button>
                  </div>
                </Alert>
              </div>
            </div>
          )}

          <div className="row">
            <div className="col-12">
              <RfidTagsTable
                tags={tags}
                pagination={pagination}
                currentPage={currentPage}
                pageSize={pageSize}
                pageSizeOptions={pageSizeOptions}
                isLoading={isLoading}
                hasActiveFilters={hasActiveFilters}
                unlinkingCardId={unlinkingCardId}
                togglingCardId={togglingCardId}
                canToggleActive={canToggleActive}
                onPageChange={setCurrentPage}
                onPageSizeChange={handlePageSizeChange}
                onUnlink={handleUnlinkCard}
                onLink={handleAssignClick}
                onToggleActive={handleToggleActive}
              />
            </div>
          </div>

          <RfidTagAssignModal
            isOpen={tagToAssign !== null}
            tag={tagToAssign}
            users={users}
            isLoadingUsers={isLoadingUsers}
            isAssigning={isAssigningCard}
            onConfirm={handleConfirmAssign}
            onClose={closeAssignModal}
          />

          <RfidTagDeactivateConfirmModal
            isOpen={tagToDeactivate !== null}
            tag={tagToDeactivate}
            isSaving={togglingCardId === tagToDeactivate?.cardId}
            onConfirm={handleConfirmDeactivate}
            onClose={closeDeactivateModal}
          />

          <RfidTagCreateModal
            isOpen={isCreateModalOpen}
            defaultInstitutionId={
              filters.institutionId ||
              (modalInstitutions.length === 1 ? modalInstitutions[0].id : '')
            }
            institutions={modalInstitutions}
            users={users}
            isLoadingUsers={isLoadingUsers}
            isSaving={isCreatingCard}
            onConfirm={handleCreateCard}
            onClose={closeCreateModal}
          />
        </>
      )}
    </div>
  );
};

export default RfidTags;
